import React, { useState, useEffect } from 'react';
import { Star, TrendingUp, MapPin, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { clientReviews } from '../mockData';

const ReviewsCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % clientReviews.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying, clientReviews.length]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % clientReviews.length);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + clientReviews.length) % clientReviews.length);
    setIsAutoPlaying(false);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  return (
    <section id="reviews" className="relative bg-gradient-to-b from-black via-slate-950 to-black py-24 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(16, 185, 129, 0.15) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      {/* Glow Effects */}
      <div className="absolute top-20 right-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl"></div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full mb-6">
            <Star className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-400 text-sm font-semibold tracking-wide">CLIENT SUCCESS STORIES</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6">
            What Our{' '}
            <span className="bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Traders Say
            </span>
          </h2>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">
            Real traders. Real results. See why thousands trust Marketwise Solutions for their automated trading journey.
          </p>
        </div>

        {/* Carousel Container */}
        <div className="max-w-6xl mx-auto relative">
          {/* Main Carousel */}
          <div className="relative bg-gradient-to-br from-slate-900/80 to-slate-950/80 backdrop-blur-xl border border-slate-800/50 rounded-3xl p-8 lg:p-16 shadow-2xl overflow-hidden">
            {/* Quote Icon */}
            <div className="absolute top-8 left-8 opacity-10">
              <Quote className="w-32 h-32 text-emerald-400" />
            </div>

            {/* Review Content */}
            <div className="relative z-10">
              {clientReviews.map((review, index) => (
                <div
                  key={review.id}
                  className={`transition-opacity duration-500 ${
                    index === currentIndex ? 'opacity-100' : 'opacity-0 absolute inset-0 pointer-events-none'
                  }`}
                >
                  {/* Rating Stars */}
                  <div className="flex justify-center gap-2 mb-8">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={`star-${review.id}-${i}`} className="w-8 h-8 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>

                  {/* Review Text */}
                  <p className="text-slate-200 text-xl sm:text-2xl leading-relaxed mb-12 text-center max-w-4xl mx-auto font-light italic">
                    "{review.review}"
                  </p>

                  {/* Profit Badge - Prominent */}
                  <div className="flex justify-center mb-12">
                    <div className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-emerald-500/20 via-emerald-400/20 to-cyan-500/20 border-2 border-emerald-500/40 rounded-2xl shadow-xl shadow-emerald-500/20">
                      <TrendingUp className="w-7 h-7 text-emerald-400" />
                      <span className="text-emerald-400 font-black text-3xl tracking-tight">{review.profit}</span>
                    </div>
                  </div>

                  {/* Client Info */}
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-6 pt-8 border-t border-slate-800/50">
                    {/* Avatar */}
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center shadow-xl shadow-emerald-500/30">
                      <span className="text-white font-black text-2xl">{review.avatar}</span>
                    </div>

                    {/* Client Details */}
                    <div className="text-center sm:text-left">
                      <h4 className="text-white font-black text-2xl mb-1">{review.name}</h4>
                      <p className="text-emerald-400 font-semibold text-lg mb-2">{review.role}</p>
                      <div className="flex items-center justify-center sm:justify-start gap-2 text-slate-500">
                        <MapPin className="w-4 h-4" />
                        <span className="text-sm">{review.location}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-slate-800/80 hover:bg-emerald-500/20 border border-slate-700 hover:border-emerald-500/50 flex items-center justify-center transition-all duration-300 group"
              aria-label="Previous review"
            >
              <ChevronLeft className="w-6 h-6 text-slate-400 group-hover:text-emerald-400" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-slate-800/80 hover:bg-emerald-500/20 border border-slate-700 hover:border-emerald-500/50 flex items-center justify-center transition-all duration-300 group"
              aria-label="Next review"
            >
              <ChevronRight className="w-6 h-6 text-slate-400 group-hover:text-emerald-400" />
            </button>
          </div>

          {/* Indicators */}
          <div className="flex justify-center gap-3 mt-8">
            {clientReviews.map((review, index) => (
              <button
                key={`indicator-${review.id}`}
                onClick={() => goToSlide(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === currentIndex
                    ? 'w-12 h-3 bg-gradient-to-r from-emerald-500 to-cyan-500'
                    : 'w-3 h-3 bg-slate-700 hover:bg-slate-600'
                }`}
                aria-label={`Go to review ${index + 1}`}
              />
            ))}
          </div>

          {/* Auto-play Toggle */}
          <div className="text-center mt-6">
            <button
              onClick={() => setIsAutoPlaying(!isAutoPlaying)}
              className="text-slate-500 hover:text-emerald-400 text-sm font-medium transition-colors"
            >
              {isAutoPlaying ? '⏸ Pause' : '▶ Auto-play'}
            </button>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-slate-400 mb-6 text-lg">
            Join thousands of successful traders worldwide
          </p>
          <a
            href="#pricing"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-black font-bold rounded-xl text-lg transition-all duration-300 shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50"
          >
            Start Your Success Story
          </a>
        </div>
      </div>
    </section>
  );
};

export default ReviewsCarousel;
